import { UnsupportedUploadTypeError } from '@client/components/ajax';
import { Reference } from '@apollo/client';
import { getStoreKeyName } from '@apollo/client/utilities';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  CommunityReplyFragmentDoc,
  MediaInput,
  useAddCommunityReplyMutation,
} from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { PostId } from '../model/post.types';
import { ReplyId } from '../model/reply.types';
import { createOptimisticReply } from '../lib/createOptimisticReply';
import { modifyPostCommentCount } from '../lib/modifyPostCommentCount';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';
import { uploadMediaInputs } from '../lib/mediaInputs';
import { useCommunityCreatorIdentity } from './useCommunityCreatorIdentity';

const REPLIES_STORE_KEY = getStoreKeyName('replies', {
  direction: undefined,
  lastId: undefined,
});

export type ReplyMediaDraft = { files: File[]; videoUuids: string[] };

// The mutation selects only the fields the create resolver populates; the
// server rejects isSensitiveContent/reactions on a freshly created reply, so
// the cache entry merges the returned fields with new-reply defaults.
export const useAddReply = (
  communityId: CommunityId,
  postId: PostId,
  canModerate: boolean,
  influencerId?: string,
) => {
  const [addReplyMutation] = useAddCommunityReplyMutation();
  const identity = useCommunityCreatorIdentity(influencerId);
  const creatorId = resolveInfluencerId(influencerId);

  return useCallback(
    async (
      text: string,
      media?: ReplyMediaDraft,
      targetReplyId?: ReplyId,
      threadRootId?: ReplyId,
    ): Promise<boolean> => {
      // targetReplyId is the reply the user actually answered (may be an L2)
      // and is what the server needs to notify the right author. The
      // optimistic entry keeps the L1 thread root so it renders in the right
      // thread instead of flashing as an orphan until the server responds.
      const optimistic = createOptimisticReply(
        text,
        identity,
        threadRootId ?? targetReplyId ?? postId,
      );

      try {
        let mediaInput: MediaInput[] | null = null;
        if (media && (media.files.length > 0 || media.videoUuids.length > 0)) {
          if (creatorId === null) throw new Error('Missing creator id');
          mediaInput = await uploadMediaInputs(
            media.files,
            media.videoUuids,
            creatorId,
          );
        }

        await addReplyMutation({
          variables: {
            communityId,
            postId,
            text,
            parentReplyId: targetReplyId ?? null,
            media: mediaInput,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            addReply: {
              __typename: 'Reply',
              id: optimistic.id,
              text: optimistic.text,
              createdAt: optimistic.createdAt,
              updatedAt: null,
              parentId: optimistic.parentId,
              media: [],
              author: {
                __typename: 'Author',
                id: optimistic.author.id,
                username: optimistic.author.username,
                badges: optimistic.author.badges,
                profilePicture: optimistic.author.profilePicture,
              },
            },
          },
          update: (cache, { data }) => {
            const reply = data?.addReply;
            if (!reply) return;

            const ref = cache.writeFragment({
              fragment: CommunityReplyFragmentDoc,
              fragmentName: 'CommunityReply',
              variables: { canModerate },
              data: {
                ...optimistic,
                id: reply.id,
                text: reply.text,
                createdAt: reply.createdAt,
                parentId: reply.parentId ?? null,
                media: reply.media,
                author: {
                  ...optimistic.author,
                  id: reply.author.id,
                  username: reply.author.username,
                  badges: reply.author.badges,
                  profilePicture: reply.author.profilePicture,
                },
              },
            });

            cache.modify<{ replies: Reference[] }>({
              id: cache.identify({ __typename: 'Post', id: postId }),
              fields: {
                replies: (
                  existing: readonly Reference[] = [],
                  { storeFieldName, readField },
                ) => {
                  if (storeFieldName !== REPLIES_STORE_KEY || !ref) {
                    return existing;
                  }
                  const alreadyListed = existing.some(
                    (item) => readField('id', item) === reply.id,
                  );
                  return alreadyListed ? existing : [ref, ...existing];
                },
              },
            });
            modifyPostCommentCount(cache, postId, 1);
          },
        });
        toast.success('Reply added successfully!');
        return true;
      } catch (error) {
        toast.error(
          error instanceof UnsupportedUploadTypeError
            ? error.message
            : 'Something went wrong',
        );
        return false;
      }
    },
    [addReplyMutation, communityId, postId, canModerate, identity, creatorId],
  );
};
